import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Layers, Minus, Plus, UserPlus } from 'lucide-react';
import { useCutierStore } from '../../store/useCutierStore';
import {
  fetchLeadsByMonth,
  saveBulkLeads,
  saveCorrectedLeads,
  saveSingleLead,
  type IngestionResult,
} from '../../services/cutierFirebaseService';
import CutierInvalidAddressesModal, {
  type CutierInvalidAddressEntry,
  loadInvalidQueue,
  makeInvalidEntry,
  persistInvalidQueue,
} from './CutierInvalidAddressesModal';

type IngestMode = 'bulk' | 'single';

const inputClass =
  'w-full rounded-xl border border-pe-border-strong bg-pe-input p-3 text-sm text-pe-text placeholder-pe-muted focus:border-[#E6C299] focus:outline-none';
const goldBtn =
  'w-full rounded-xl bg-[#E6C299] px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#030303] hover:bg-[#d4b086] disabled:opacity-50';

const mapInvalidFromResult = (
  entries: { raw: string; reason: string }[]
): CutierInvalidAddressEntry[] =>
  entries.map((e) => makeInvalidEntry(e.raw, e.reason, e.raw.includes('@') ? e.raw : undefined));

const mergeInvalidEntries = (
  existing: CutierInvalidAddressEntry[],
  incoming: CutierInvalidAddressEntry[]
): CutierInvalidAddressEntry[] => {
  const byKey = new Map<string, CutierInvalidAddressEntry>();
  for (const entry of existing) byKey.set(entry.id, entry);
  for (const entry of incoming) byKey.set(entry.id, entry);
  return Array.from(byKey.values());
};

const CutierLeadIngestion: React.FC = () => {
  const selectedMonth = useCutierStore((s) => s.selectedMonth);
  const setLeads = useCutierStore((s) => s.setLeads);

  const [mode, setMode] = useState<IngestMode>('single');
  const [bulkText, setBulkText] = useState('');
  const [singleEmail, setSingleEmail] = useState('');
  const [singleName, setSingleName] = useState('');
  const [singleQuantity, setSingleQuantity] = useState(1);
  const [busy, setBusy] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkResult, setBulkResult] = useState<IngestionResult | null>(null);
  const [addedCount, setAddedCount] = useState<number | null>(null);
  const [repeatOrderCount, setRepeatOrderCount] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [invalidModalOpen, setInvalidModalOpen] = useState(false);
  const [invalidModalEntries, setInvalidModalEntries] = useState<CutierInvalidAddressEntry[]>([]);

  const refreshLeads = async () => {
    const rows = await fetchLeadsByMonth(selectedMonth);
    setLeads(rows);
  };

  const setInvalidQueue = useCallback(
    (entries: CutierInvalidAddressEntry[]) => {
      setInvalidModalEntries(entries);
      persistInvalidQueue(selectedMonth, entries);
    },
    [selectedMonth]
  );

  useEffect(() => {
    setInvalidQueue(loadInvalidQueue(selectedMonth));
  }, [selectedMonth, setInvalidQueue]);

  const appendInvalidEntries = (incoming: CutierInvalidAddressEntry[]) => {
    if (incoming.length === 0) return;
    setInvalidModalEntries((prev) => {
      const merged = mergeInvalidEntries(prev, incoming);
      persistInvalidQueue(selectedMonth, merged);
      return merged;
    });
    setInvalidModalOpen(true);
  };

  const openInvalidModal = (entries: CutierInvalidAddressEntry[]) => {
    appendInvalidEntries(entries);
  };

  const clearImportFeedback = () => {
    setAddedCount(null);
    setRepeatOrderCount(null);
    setBulkResult(null);
    setImportProgress(null);
    setErrorMsg('');
  };

  const handleBulk = async (e: React.FormEvent) => {
    e.preventDefault();
    clearImportFeedback();
    if (!bulkText.trim()) return;
    setBusy(true);
    try {
      const result = await saveBulkLeads(bulkText, selectedMonth, (done, total) => {
        setImportProgress({ done, total });
      });
      setBulkResult(result);
      setAddedCount(result.addedCount);
      setRepeatOrderCount(result.repeatOrderCount);

      if (result.invalidEntries.length > 0) {
        openInvalidModal(mapInvalidFromResult(result.invalidEntries));
      }

      if (result.addedCount > 0) {
        setBulkText('');
        await refreshLeads();
      }

      if (result.addedCount === 0 && result.invalidCount === 0 && result.repeatOrderCount === 0) {
        setErrorMsg('Im eingefügten Text wurde keine importierbare E-Mail-Adresse gefunden.');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Import fehlgeschlagen.');
    } finally {
      setBusy(false);
      setImportProgress(null);
    }
  };

  const handleSingle = async (e: React.FormEvent) => {
    e.preventDefault();
    clearImportFeedback();
    setBusy(true);
    try {
      const quantity = Math.max(1, Math.floor(singleQuantity));
      const result = await saveSingleLead(
        singleEmail,
        singleName || undefined,
        selectedMonth,
        quantity
      );
      if (result.isRepeatOrder) {
        setAddedCount(quantity);
        setRepeatOrderCount(quantity);
        setSingleEmail('');
        setSingleName('');
        setSingleQuantity(1);
        await refreshLeads();
        return;
      }
      if (result.success) {
        setAddedCount(quantity);
        setRepeatOrderCount(0);
        setSingleEmail('');
        setSingleName('');
        setSingleQuantity(1);
        await refreshLeads();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Erfassung fehlgeschlagen — bitte E-Mail-Adresse prüfen.';
      setErrorMsg(message);
      openInvalidModal([
        makeInvalidEntry(
          singleEmail.trim() || '(leer)',
          message,
          singleEmail.trim(),
          singleName.trim() || undefined
        ),
      ]);
    } finally {
      setBusy(false);
    }
  };

  const handleRetryInvalid = async () => {
    if (invalidModalEntries.length === 0) return;
    setRetryBusy(true);
    clearImportFeedback();
    try {
      const result = await saveCorrectedLeads(
        invalidModalEntries.map((e) => ({
          email: e.email,
          name: e.name,
          orderCount: 1,
        })),
        selectedMonth,
        (done, total) => setImportProgress({ done, total })
      );

      const stillInvalid = result.stillInvalid.map((e) =>
        makeInvalidEntry(e.raw, e.reason, e.email, e.name)
      );
      setInvalidQueue(stillInvalid);

      if (result.savedCount > 0) {
        setAddedCount(result.savedCount);
        setRepeatOrderCount(result.repeatOrderCount);
        await refreshLeads();
      }

      if (result.savedCount > 0 && stillInvalid.length === 0) {
        setInvalidModalOpen(false);
      } else if (result.savedCount === 0 && stillInvalid.length > 0) {
        setErrorMsg('Keine Adresse konnte übernommen werden — bitte Eingaben prüfen.');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Erneuter Versuch fehlgeschlagen.');
    } finally {
      setRetryBusy(false);
      setImportProgress(null);
    }
  };

  const invalidCount = invalidModalEntries.length;

  return (
    <>
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setMode('single');
              clearImportFeedback();
            }}
            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-[10px] font-black uppercase tracking-widest ${
              mode === 'single'
                ? 'border-[#E6C299]/50 bg-[#E6C299]/10 text-pe-accent-text'
                : 'border-pe-border-strong bg-pe-input text-pe-muted hover:text-pe-text'
            }`}
          >
            <UserPlus className="h-4 w-4" />
            Einzel-Erfassung
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('bulk');
              clearImportFeedback();
            }}
            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-[10px] font-black uppercase tracking-widest ${
              mode === 'bulk'
                ? 'border-[#E6C299]/50 bg-[#E6C299]/10 text-pe-accent-text'
                : 'border-pe-border-strong bg-pe-input text-pe-muted hover:text-pe-text'
            }`}
          >
            <Layers className="h-4 w-4" />
            Bulk-Import
          </button>
        </div>

        {mode === 'single' ? (
          <form onSubmit={handleSingle} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted">
                E-Mail-Adresse *
              </span>
              <input
                type="email"
                required
                value={singleEmail}
                onChange={(e) => setSingleEmail(e.target.value)}
                className={inputClass}
                placeholder="name@salon.de"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted">
                Kundenname (optional)
              </span>
              <input
                type="text"
                value={singleName}
                onChange={(e) => setSingleName(e.target.value)}
                className={inputClass}
                placeholder="z. B. Max Mustermann"
              />
            </label>
            <div className="rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/10 p-3">
              <span className="mb-2 block text-[10px] font-black uppercase tracking-wider text-pe-accent-text">
                Scheren / Bestellungen
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSingleQuantity((n) => Math.max(1, n - 1))}
                  className="flex h-12 w-12 items-center justify-center rounded-xl border border-pe-border-strong bg-pe-input text-pe-text hover:text-white"
                  aria-label="Eine Schere weniger"
                >
                  <Minus className="h-5 w-5" />
                </button>
                <div className="min-w-[4.5rem] text-center">
                  <div className="text-3xl font-black text-pe-accent-text">{singleQuantity}</div>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-pe-muted">
                    {singleQuantity === 1 ? 'Schere' : 'Scheren'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSingleQuantity((n) => Math.min(99, n + 1))}
                  className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#E6C299]/50 bg-[#E6C299]/20 text-pe-accent-text hover:bg-[#E6C299]/30"
                  aria-label="Eine Schere mehr"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-[10px] text-pe-muted">
                3 Scheren = 3 × 50 €. Dieselbe Mail erneut erfassen addiert weiter. Einladungs-Mail nur
                beim ersten Mal.
              </p>
            </div>
            <button type="submit" disabled={busy || !singleEmail.trim()} className={goldBtn}>
              {busy ? 'Prüfe & speichere…' : 'Lead erfassen'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleBulk} className="space-y-3">
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              placeholder={
                'Eine Zeile pro Person, z. B.:\nMax Mustermann <max@salon.de>\nLisa Müller, lisa@salon.de\nDoppelte Zeilen = mehrere Scheren pro Kunde'
              }
              className={inputClass}
            />
            <p className="text-[10px] text-pe-muted">
              Namen werden mitgespeichert. Dieselbe E-Mail mehrfach in der Liste zählt als mehrere
              Bestellungen. Domains ohne Mailserver werden in die Warteschlange gelegt.
            </p>
            <button type="submit" disabled={busy || !bulkText.trim()} className={goldBtn}>
              {busy
                ? importProgress
                  ? `Importiere ${importProgress.done} / ${importProgress.total}…`
                  : 'Prüfe Adressen…'
                : 'Leads importieren'}
            </button>
          </form>
        )}

        {busy && !importProgress ? (
          <p className="text-xs uppercase tracking-widest text-pe-muted">Bitte warten…</p>
        ) : null}

        {invalidCount > 0 ? (
          <button
            type="button"
            onClick={() => setInvalidModalOpen(true)}
            className="flex w-full items-center justify-between rounded-xl border border-red-500/50 bg-red-900/10 px-4 py-3 transition-colors hover:bg-red-900/20"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 animate-pulse text-red-500" />
              <span className="text-sm font-bold text-red-400">
                {invalidCount} fehlerhafte {invalidCount === 1 ? 'Adresse' : 'Adressen'} in Warteschlange
              </span>
            </div>
            <span className="rounded bg-red-500 px-2 py-0.5 text-[10px] font-black text-white shadow-[0_0_10px_rgba(239,68,68,0.5)]">
              Bearbeiten
            </span>
          </button>
        ) : null}

        {errorMsg && invalidCount === 0 ? (
          <div className="rounded-xl border-2 border-red-500 bg-red-950/50 p-4 text-sm text-red-100">
            <div className="mb-2 flex items-center gap-2 font-bold uppercase tracking-wider text-red-300">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Hinweis
            </div>
            <p>{errorMsg}</p>
          </div>
        ) : null}

        {errorMsg && invalidCount > 0 && repeatOrderCount == null && addedCount == null ? (
          <div className="rounded-xl border border-amber-600/50 bg-amber-950/30 p-3 text-sm text-amber-200">
            {errorMsg}
          </div>
        ) : null}

        {addedCount != null && addedCount > 0 ? (
          <div className="rounded-lg border border-emerald-800 bg-emerald-950/40 p-3 text-sm text-emerald-400">
            {mode === 'single'
              ? repeatOrderCount && repeatOrderCount > 0
                ? `${addedCount} weitere Bestellung(en) erfasst (Abrechnung aktualisiert).`
                : 'Lead erfasst & Einladungs-Mail eingereiht.'
              : `${addedCount} Bestellung(en) importiert${repeatOrderCount ? ` (${repeatOrderCount} bei bestehenden Kunden)` : ''}.`}
          </div>
        ) : null}

        {bulkResult && addedCount === 0 && invalidCount === 0 && repeatOrderCount != null && repeatOrderCount > 0 ? (
          <div className="rounded-xl border-2 border-amber-500/60 bg-amber-950/40 p-4 text-sm text-amber-200">
            <p className="font-bold uppercase tracking-wider text-amber-300">Keine neuen Adressen</p>
            <p className="mt-1">
              {repeatOrderCount} weitere Bestellung(en) bei bereits erfassten Kunden hinzugefügt.
            </p>
          </div>
        ) : null}

        {repeatOrderCount != null && repeatOrderCount > 0 && addedCount != null && addedCount > repeatOrderCount ? (
          <div className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 p-3 text-sm text-pe-accent-text">
            Davon {repeatOrderCount} Bestellung(en) bei bereits erfassten Adressen
          </div>
        ) : null}

        {bulkResult && bulkResult.mailFailedCount > 0 ? (
          <div className="rounded-xl border-2 border-amber-500/60 bg-amber-950/40 p-4 text-sm text-amber-200">
            <p className="font-bold text-amber-300">
              {bulkResult.mailFailedCount} Lead(s) gespeichert, aber Einladungs-Mail konnte nicht eingereiht werden.
            </p>
            <p className="mt-1 text-amber-200/90">
              Bitte Admin informieren — Leads stehen in der Liste mit Status „Offen“.
            </p>
          </div>
        ) : null}

        {bulkResult && bulkResult.failedEntries.length > 0 ? (
          <div className="rounded-xl border-2 border-red-500 bg-red-950/50 p-4 text-sm text-red-100">
            <p className="mb-2 font-bold uppercase tracking-wider text-red-300">Speichern fehlgeschlagen</p>
            <ul className="space-y-1 text-xs">
              {bulkResult.failedEntries.map((entry) => (
                <li key={entry.email}>
                  <span className="font-mono">{entry.email}</span> — {entry.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <CutierInvalidAddressesModal
        isOpen={invalidModalOpen}
        entries={invalidModalEntries}
        importedCount={addedCount ?? 0}
        busy={retryBusy}
        onClose={() => setInvalidModalOpen(false)}
        onUpdateEntry={(id, patch) => {
          setInvalidModalEntries((prev) => {
            const next = prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry));
            persistInvalidQueue(selectedMonth, next);
            return next;
          });
        }}
        onRemoveEntry={(id) => {
          setInvalidModalEntries((prev) => {
            const next = prev.filter((entry) => entry.id !== id);
            persistInvalidQueue(selectedMonth, next);
            return next;
          });
        }}
        onDismissAll={() => {
          setInvalidQueue([]);
          setInvalidModalOpen(false);
        }}
        onRetry={() => void handleRetryInvalid()}
      />
    </>
  );
};

export default CutierLeadIngestion;
